import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    ActivityIndicator,
    FlatList,
    StyleSheet,
    TouchableOpacity,
    View,
    useWindowDimensions,
} from "react-native";
import { Card } from "react-native-paper";
import { useTheme } from "@react-navigation/native";
import { createCommonStyles } from "../../../styles/commonStyles";
import CustomLabel from "../../../components/CustomLabel/CustomLabel";
import CustomInput from "../../../components/CustomInput";
import CustomButton from "../../../components/CustomButton";
import { useTranslation } from "react-i18next";
import { useCompany } from "../../../hooks/useCompany";
import { showMessage } from "react-native-flash-message";
import Ionicons from "@react-native-vector-icons/ionicons/static";
import Swal from "sweetalert2";
import { v4 as uuidv4 } from "uuid";

type EmployeeRatesItem = {
    employeeID: string;
    rateID: string;
    dor: string;
    rate: number | string;
    company: string;
    comments?: string;
    userid?: string;
    doc?: string;
    rowguid?: string;
    __tempKey?: string;
    __isNew?: boolean;
    __markedForDelete?: boolean;
    __isPrevious?: boolean;
};

type SelectedEmployee = {
    employeeID?: string;
    employeeId?: string;
    empid?: string;
    id?: string;
    code?: string;
    employeeNo?: string;
};

type EmployeeRatesScreenProps = {
    selectedCompany?: string;
    selectedEmployee?: SelectedEmployee | string;
};

type EmployeeRateRowProps = {
    item: EmployeeRatesItem;
    styles: ReturnType<typeof makeStyles>;
    handleEditPress: (
        item: EmployeeRatesItem,
        key: keyof EmployeeRatesItem,
        value: string
    ) => void;
    handleDelete: (item: EmployeeRatesItem) => void;
    isChanged: boolean;
    isDeleted: boolean;
};

const formatDateToYYYYMMDD = (value?: string | Date | null): string => {
    if (!value) return "";
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toISOString().slice(0, 10);
};

const isValidYYYYMMDD = (value: string): boolean => {
 const trimmed = value.trim();

 if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return false;

 const [year, month, day] = trimmed.split("-").map(Number);
 const date = new Date(year, month - 1, day);

 return (
 date.getFullYear() === year &&
 date.getMonth() === month - 1 &&
 date.getDate() === day
 );
};

const normalizeRateInput = (value: string): string => {
    const cleaned = value.replace(",", ".").replace(/[^0-9.]/g, "");
    const parts = cleaned.split(".");
    if (parts.length <= 1) return cleaned;
    return `${parts[0]}.${parts.slice(1).join("")}`;
};

const getEmployeeIdValue = (selectedEmployee?: SelectedEmployee | string): string => {
    if (!selectedEmployee) return "";
    if (typeof selectedEmployee === "string") return selectedEmployee;

    return (
        selectedEmployee.employeeID ||
        selectedEmployee.employeeId ||
        selectedEmployee.empid ||
        selectedEmployee.employeeNo ||
        selectedEmployee.id ||
        selectedEmployee.code ||
        ""
    );
};

const extractArray = (response: any): any[] => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.items)) return response.items;
    if (Array.isArray(response?.value)) return response.value;
    return [];
};

const normalizeEmployeeRateItem = (
    item: any,
    selectedCompany: string,
    employeeId: string,
    isPrevious: boolean
): EmployeeRatesItem => {
    return {
        employeeID: item.employeeID ?? item.employeeId ?? employeeId,
        rateID: item.rateID ?? item.rateId ?? "",
        dor: formatDateToYYYYMMDD(item.dor ?? item.Dor),
        rate: item.rate ?? item.Rate ?? "",
        company: item.company ?? item.Company ?? selectedCompany,
        comments: item.comments ?? item.Comments ?? "",
        userid: item.userid ?? item.userId,
        doc: item.doc ?? item.Doc,
        rowguid: item.rowguid ?? item.rowGuid ?? item.RowGuid,
        __isNew: false,
        __markedForDelete: false,
        __isPrevious: isPrevious,
    };
};

const sortEmployeeRates = (data: EmployeeRatesItem[]): EmployeeRatesItem[] => {
    return [...data].sort((a, b) => {
        if (a.__isPrevious !== b.__isPrevious) {
            return Number(a.__isPrevious) - Number(b.__isPrevious);
        }

        const dateA = new Date(a.dor).getTime();
        const dateB = new Date(b.dor).getTime();

        if (dateA !== dateB) {
            return dateB - dateA;
        }

        return (a.rateID || "").localeCompare(b.rateID || "");
    });
};

const dedupeByRowGuidOrComposite = (data: EmployeeRatesItem[]): EmployeeRatesItem[] => {
    const seen = new Set<string>();

    return data.filter((item) => {
        const key =
            item.rowguid ||
            `${item.company}-${item.employeeID}-${item.rateID}-${item.dor}-${item.__isPrevious ? "P" : "C"}`;

        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

const EmployeeRatesListRow = React.memo(
    ({
        item,
        styles,
        handleEditPress,
        handleDelete,
        isChanged,
        isDeleted,
    }: EmployeeRateRowProps) => {
        const isReadOnly = item.__isPrevious || isDeleted;

        return (
            <View
                style={[
                    styles.rowContainer,
                    item.__isPrevious && styles.previousRow,
                    /* todo: add later if also added in other pages isChanged && styles.changedRow,*/
                    isDeleted && styles.deletedRow,
                ]}
            >
                <View style={[styles.row, isReadOnly && styles.disabledRowContent]}>
                    <View style={styles.item}>
                        <CustomInput
                            value={item.rateID ?? ""}
                            setValue={(text: string) => handleEditPress(item, "rateID", text)}
                            editable={!isReadOnly}
                            maxLen={1}
                            type="MAININPUT"
                        />
                    </View>

                    <View style={styles.item}>
                        <CustomInput
                            value={item.dor ?? ""}
                            setValue={(text: string) => handleEditPress(item, "dor", text)}
                            editable={!isReadOnly}
                            maxLen={10}
                            type="MAININPUT"
                        />
                    </View>

                    <View style={styles.item}>
                        <CustomInput
                            value={String(item.rate ?? "")}
                            setValue={(text: string) => handleEditPress(item, "rate", text)}
                            editable={!isReadOnly}
                            maxLen={20}
                            type="MAININPUT"
                            keyboardType="numeric"
                        />
                    </View>

                    <View style={styles.commentsItem}>
                        <CustomInput
                            value={item.comments ?? ""}
                            setValue={(text: string) => handleEditPress(item, "comments", text)}
                            editable={!isReadOnly}
                            maxLen={250}
                            type="MAININPUT"
                        />
                    </View>

                    <View style={styles.statusItem}>
                        <CustomLabel
                            value={item.__isPrevious ? "Previous" : "Current"}
                            type="SUBTITLE"
                        />
                    </View>

                    <View style={styles.actionContainer}>
                        {!item.__isPrevious && (
                            <CustomButton
                                iconName={isDeleted ? "refresh-outline" : "trash-outline"}
                                onPress={() => handleDelete(item)}
                                show="HOME_BELL"
                                type="HOME_BELL"
                                textTop="2"
                                sz={30}
                            />
                        )}
                    </View>
                </View>
            </View>
        );
    }
);

const HRAdminEmployeeRates: React.FC<EmployeeRatesScreenProps> = (props) => {
    const [rates, setRates] = useState<EmployeeRatesItem[]>([]);
    const [originalRates, setOriginalRates] = useState<EmployeeRatesItem[]>([]);
    const [changes, setChanges] = useState<Record<string, EmployeeRatesItem>>({});
    const [deletedItems, setDeletedItems] = useState<EmployeeRatesItem[]>([]);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const ref = useRef<FlatList<EmployeeRatesItem>>(null);

    const { t } = useTranslation();
    const { colors } = useTheme();
    const { width, height } = useWindowDimensions();
    const styles = useMemo(() => makeStyles(colors, width, height), [colors, width, height]);
    const common = createCommonStyles(colors);

    const { selectedCompany, selectedEmployee } = props;
    const employeeId = useMemo(() => getEmployeeIdValue(selectedEmployee), [selectedEmployee]);

    const {
        getEmployeeRates,
        getPrevEmployeeRates,
        postEmployeeRates,
        putEmployeeRates,
        deleteEmployeeRates,
    } = useCompany();

    const getRowKey = useCallback((item: EmployeeRatesItem): string => {
        return item.rowguid || item.__tempKey || `${item.company}-${item.employeeID}-${item.rateID}-${item.dor}`;
    }, []);

    const scrollToTop = useCallback(() => {
        ref.current?.scrollToOffset({ offset: 0, animated: true });
    }, []);

    const loadRates = useCallback(async (silent = false) => {
        if (!selectedCompany || !employeeId) {
            setRates([]);
            setOriginalRates([]);
            setChanges({});
            setDeletedItems([]);
            setIsInitialLoading(false);
            return;
        }

        if (!silent) {
            setIsInitialLoading(true);
        }

        try {
            const [currentResponse, previousResponse] = await Promise.all([
                getEmployeeRates(selectedCompany, employeeId),
                getPrevEmployeeRates(selectedCompany, employeeId),
            ]);

            const currentArray = extractArray(currentResponse);
            const previousArray = extractArray(previousResponse);

            const normalizedCurrent = currentArray.map((item) =>
                normalizeEmployeeRateItem(item, selectedCompany, employeeId, false)
            );

            const normalizedPrevious = previousArray.map((item) =>
                normalizeEmployeeRateItem(item, selectedCompany, employeeId, true)
            );

            const merged = dedupeByRowGuidOrComposite(
                sortEmployeeRates([...normalizedCurrent, ...normalizedPrevious])
            );

            setRates(merged);
            setOriginalRates(merged);
            setChanges({});
            setDeletedItems([]);
        } catch (error) {
            showMessage({
                message: t("common:errorLoadingData"),
                type: "danger",
                duration: 3000,
            });
            setRates([]);
            setOriginalRates([]);
        } finally {
            setIsInitialLoading(false);
        }
    }, [selectedCompany, employeeId, getEmployeeRates, getPrevEmployeeRates, t]);

    useEffect(() => {
        let mounted = true;

        const run = async () => {
            if (!selectedCompany || !employeeId) {
                setRates([]);
                setOriginalRates([]);
                setChanges({});
                setDeletedItems([]);
                setIsInitialLoading(false);
                return;
            }

            setIsInitialLoading(true);

            try {
                const [currentResponse, previousResponse] = await Promise.all([
                    getEmployeeRates(selectedCompany, employeeId),
                    getPrevEmployeeRates(selectedCompany, employeeId),
                ]);

                if (!mounted) return;

                const currentArray = extractArray(currentResponse);
                const previousArray = extractArray(previousResponse);

                const normalizedCurrent = currentArray.map((item) =>
                    normalizeEmployeeRateItem(item, selectedCompany, employeeId, false)
                );

                const normalizedPrevious = previousArray.map((item) =>
                    normalizeEmployeeRateItem(item, selectedCompany, employeeId, true)
                );

                const merged = dedupeByRowGuidOrComposite(
                    sortEmployeeRates([...normalizedCurrent, ...normalizedPrevious])
                );

                setRates(merged);
                setOriginalRates(merged);
                setChanges({});
                setDeletedItems([]);
            } catch (error) {
                if (!mounted) return;

                showMessage({
                    message: t("common:errorLoadingData"),
                    type: "danger",
                    duration: 3000,
                });
                setRates([]);
                setOriginalRates([]);
            } finally {
                if (mounted) {
                    setIsInitialLoading(false);
                }
            }
        };

        run();

        return () => {
            mounted = false;
        };
    }, [selectedCompany, employeeId]);

    const removeFromChangesAfterSave = useCallback(() => {
        setChanges({});
        setDeletedItems([]);
    }, []);

    const isRowChanged = useCallback(
        (item: EmployeeRatesItem) => {
            const rowKey = getRowKey(item);
            return Boolean(changes[rowKey]) && !item.__markedForDelete && !item.__isPrevious;
        },
        [changes, getRowKey]
    );

    const isRowDeleted = useCallback((item: EmployeeRatesItem) => {
        return Boolean(item.__markedForDelete);
    }, []);

    const addNewRate = useCallback(() => {
        if (!selectedCompany || !employeeId) {
            showMessage({
                message: "Please select company and employee first.",
                type: "info",
                duration: 3000,
            });
            return;
        }

        const hasEmptyNewRow = rates.some(
            (row) =>
                row.__isNew &&
                !row.rateID?.trim() &&
                !row.comments?.trim() &&
                !String(row.rate ?? "").trim()
        );

        if (hasEmptyNewRow) {
            showMessage({
                message: t("common:multEmptyLines"),
                type: "danger",
                duration: 3000,
            });
            return;
        }

        const tempKey = uuidv4();

        const newRow: EmployeeRatesItem = {
            employeeID: employeeId,
            rateID: "",
            dor: formatDateToYYYYMMDD(new Date()),
            rate: "",
            company: selectedCompany,
            comments: "",
            __tempKey: tempKey,
            __isNew: true,
            __markedForDelete: false,
            __isPrevious: false,
        };

        setRates((prev) => sortEmployeeRates([newRow, ...prev]));
        setChanges((prev) => ({
            ...prev,
            [tempKey]: newRow,
        }));

        scrollToTop();
    }, [selectedCompany, employeeId, rates, t, scrollToTop]);

    const handleEditPress = useCallback(
        (
            item: EmployeeRatesItem,
            key: keyof EmployeeRatesItem,
            value: string
        ) => {
            if (item.__markedForDelete || item.__isPrevious) return;

            let sanitizedValue: string | number = value;

            if (key === "rateID") {
                sanitizedValue = value.toUpperCase().replace(/\s/g, "").slice(0, 1);
            }

            if (key === "dor") {
                sanitizedValue = value.replace(/[^0-9-]/g, "").slice(0, 10);
            }

            if (key === "rate") {
                sanitizedValue = normalizeRateInput(value);
            }

            if (key === "comments") {
                sanitizedValue = value.slice(0, 250);
            }

            if (item[key] === sanitizedValue) return;

            const rowKey = getRowKey(item);

            setRates((prev) =>
                prev.map((row) =>
                    getRowKey(row) === rowKey
                        ? {
                            ...row,
                            [key]: sanitizedValue,
                        }
                        : row
                )
            );

            setChanges((prev) => ({
                ...prev,
                [rowKey]: {
                    ...(prev[rowKey] ?? item),
                    [key]: sanitizedValue,
                },
            }));
        },
        [getRowKey]
    );

    const handleCancelChanges = useCallback(() => {
        setRates(originalRates);
        setChanges({});
        setDeletedItems([]);
        showMessage({
            message: t("common:changesCancelled"),
            type: "info",
            duration: 3000,
        });
    }, [originalRates, t]);

    const validateInput = useCallback((): [boolean, string] => {
        const currentRows = rates.filter((row) => !row.__markedForDelete && !row.__isPrevious);

        for (const row of currentRows) {
            if (!row.company || row.company.trim() === "" || row.company.length > 5) {
                return [false, "Company is required and must be max 5 characters."];
            }

            if (!row.employeeID || row.employeeID.trim() === "" || row.employeeID.length > 9) {
                return [false, "Employee ID is required and must be max 9 characters."];
            }

            if (!row.rateID || row.rateID.trim() === "" || row.rateID.length > 1) {
                return [false, "Rate ID is required and must be exactly 1 character."];
            }

            if (!row.dor || !isValidYYYYMMDD(row.dor)) {
                return [false, "DOR must be a valid date in YYYY-MM-DD format."];
            }

            const rateValue = String(row.rate ?? "").trim();
            if (rateValue === "") {
                return [false, "Rate is required."];
            }

            const parsedRate = Number(rateValue);
            if (Number.isNaN(parsedRate) || parsedRate < 0) {
                return [false, "Rate must be a valid non-negative number."];
            }

            if ((row.comments ?? "").length > 250) {
                return [false, "Comments must be max 250 characters."];
            }
        }

        const seen = new Set<string>();
        for (const row of currentRows) {
            const key = `${row.company.toUpperCase()}-${row.employeeID.toUpperCase()}-${row.rateID.toUpperCase()}-${row.dor}`;
            if (seen.has(key)) {
                return [false, "Duplicate employee rate found for Employee + Rate ID + DOR + Company."];
            }
            seen.add(key);
        }

        return [true, ""];
    }, [rates]);

    const handleSave = useCallback(async () => {
        try {
            setIsSaving(true);
            const [isValid, validationMessage] = validateInput();
            if (!isValid) {
                showMessage({
                    message: validationMessage,
                    type: "danger",
                    duration: 4000,
                });
                setIsSaving(false);
                return;
            }

            if (deletedItems.length > 0) {
                for (const item of deletedItems) {
                    if (!item.rowguid) continue;

                    const deleteResult = await deleteEmployeeRates({
                        RowGuid: item.rowguid,
                    });

                    if (deleteResult && deleteResult.success === false) {
                        if (deleteResult.conflict) {
                            const detailMessage =
                                deleteResult.details?.detail || t("common:cannotDeleteItem");
                            await Swal.fire({
                                title: t("common:cannotDelete"),
                                text: detailMessage,
                                icon: "error",
                                confirmButtonText: t("common:ok"),
                            });
                        } else {
                            showMessage({
                                message: t("common:failedDelete"),
                                type: "danger",
                                duration: 3000,
                            });
                        }
                        setIsSaving(false);
                        return;
                    }
                }

                showMessage({
                    message: t("common:successDelete"),
                    type: "success",
                    duration: 3000,
                });
            }

            const changedRows = Object.values(changes).filter(
                (row) => !row.__markedForDelete && !row.__isPrevious
            );

            if (changedRows.length > 0) {
                for (const change of changedRows) {
                    const isExisting = Boolean(change.rowguid);

                    if (isExisting) {
                        await putEmployeeRates({
                            RowGuid: change.rowguid,
                            RateId: change.rateID,
                            Dor: change.dor,
                            Rate: Number(change.rate),
                            Company: change.company,
                            Comments: change.comments ?? "",
                        });
                    } else {
                        await postEmployeeRates({
                            EmployeeId: change.employeeID,
                            RateId: change.rateID,
                            Rate: Number(change.rate),
                            Dor: change.dor,
                            Company: change.company,
                            Comments: change.comments ?? "",
                        });
                    }
                }

                showMessage({
                    message: t("common:successSave"),
                    type: "success",
                    duration: 3000,
                });
            }

            if (deletedItems.length === 0 && changedRows.length === 0) {
                showMessage({
                    message: t("common:noChanges"),
                    type: "info",
                    duration: 3000,
                });
            }

            removeFromChangesAfterSave();
            await loadRates(true);
        } catch (error: any) {
            const validationErrors = error?.response?.data?.errors || {};
            const messages: string[] = Object.values(validationErrors).flat() as string[];

            const duplicateError = messages.some((msg) => {
                const lower = msg.toLowerCase();
                return (
                    lower.includes("duplicate") ||
                    lower.includes("already exists") ||
                    lower.includes("unique constraint")
                );
            });

            if (duplicateError) {
                showMessage({
                    message: "Duplicate employee rate found.",
                    type: "danger",
                    duration: 5000,
                });
            } else {
                showMessage({
                    message: t("common:errorSave"),
                    type: "danger",
                    duration: 3000,
                });
            }
        } finally {
            setIsSaving(false);
        }
    }, [
        validateInput,
        deletedItems,
        changes,
        deleteEmployeeRates,
        putEmployeeRates,
        postEmployeeRates,
        removeFromChangesAfterSave,
        loadRates,
        t,
    ]);

    const handleDelete = useCallback(async (item: EmployeeRatesItem) => {
        if (!item) {
            showMessage({
                message: t("common:invalidItem"),
                type: "danger",
                duration: 3000,
            });
            return;
        }

        if (item.__isPrevious) {
            return;
        }

        const rowKey = getRowKey(item);

        if (!item.rowguid) {
            setRates((prev) => prev.filter((row) => getRowKey(row) !== rowKey));
            setChanges((prev) => {
                const next = { ...prev };
                delete next[rowKey];
                return next;
            });

            showMessage({
                message: t("common:rowDeletedLocally"),
                type: "success",
                duration: 2000,
            });
            return;
        }

        setRates((prev) =>
            prev.map((row) =>
                getRowKey(row) === rowKey
                    ? {
                        ...row,
                        __markedForDelete: !row.__markedForDelete,
                    }
                    : row
            )
        );

        setDeletedItems((prev) => {
            const alreadyDeleted = prev.some((x) => getRowKey(x) === rowKey);

            if (alreadyDeleted) {
                return prev.filter((x) => getRowKey(x) !== rowKey);
            }

            return [...prev, { ...item, __markedForDelete: true }];
        });

        setChanges((prev) => {
            const next = { ...prev };
            delete next[rowKey];
            return next;
        });
    }, [getRowKey, t]);

    const renderData = useCallback(
        (item: EmployeeRatesItem) => (
            <Card style={[common.cardstyle]}>
                <Card.Content>
                    <EmployeeRatesListRow
                        item={item}
                        styles={styles}
                        handleEditPress={handleEditPress}
                        handleDelete={handleDelete}
                        isChanged={isRowChanged(item)}
                        isDeleted={isRowDeleted(item)}
                    />
                </Card.Content>
            </Card>
        ),
        [common.cardstyle, styles, handleEditPress, handleDelete, isRowChanged, isRowDeleted]
    );

    if (isInitialLoading) {
        return (
            <View style={styles.loader}>
                <ActivityIndicator animating={true} color={colors.blue} />
            </View>
        );
    }

    return (
        <View style={styles.mainView}>

            <View style={styles.container}>
                <View style={styles.buttonsRow}>
                    <View style={styles.leftButtons} />
                    <View style={styles.rightButtons}>
                        <CustomButton
                            text={t("common:new")}
                            onPress={addNewRate}
                            type="BUTTON_ADD"
                            textTop="0"
                            sz={15}
                        />

                        {(Object.keys(changes).length > 0 || deletedItems.length > 0) && (
                            <CustomButton
                                text={t("common:saveFunction")}
                                onPress={handleSave}
                                type="BUTTON_SAVE"
                                isGradient={true}
                                textTop="0"
                                sz={15}
                            />
                        )}

                        <CustomButton
                            text={t("common:cancel")}
                            onPress={handleCancelChanges}
                            type="BUTTON_CANCEL"
                            textTop="0"
                            sz={15}
                        />
                    </View>
                </View>

                <FlatList
                    data={rates}
                    keyExtractor={(item) => getRowKey(item)}
                    renderItem={({ item }) => renderData(item)}
                    refreshing={false}
                    ref={ref}
                    ListHeaderComponent={
                        <View style={common.listTitle}>
                            <View style={styles.row}>
                                <CustomLabel value={"Rate ID"} type="SUBTITLE" />
                                <CustomLabel value={"DOR"} type="SUBTITLE" />
                                <CustomLabel value={"Rate"} type="SUBTITLE" />
                                <CustomLabel value={"Comments"} type="SUBTITLE" />
                                <CustomLabel value={"Type"} type="SUBTITLE" />
                                <View style={{ width: 40 }} />
                            </View>
                            <View style={common.divider} />
                        </View>
                    }
                    ListEmptyComponent={
                        <CustomLabel value={"No employee rates found"} type="NO_FUNCTIONS" />
                    }
                    initialNumToRender={8}
                />
            </View>

            {isSaving && (
                <View style={styles.savingOverlay}>
                    <ActivityIndicator animating={true} color={colors.blue} size="large" />
                </View>
            )}
        </View>
    );
};

const makeStyles = (colors: any, width: number, height: number) =>
    StyleSheet.create({
        container: {
            flex: 1,
        },
        mainView: {
            flex: 1,
        },
        rowContainer: {
            borderRadius: 6,
        },
        row: {
            flex: 1,
            flexDirection: "row",
            alignItems: "center",
        },
        item: {
            flex: 1,
            justifyContent: "center",
            marginRight: 6,
        },
        commentsItem: {
            flex: 2,
            justifyContent: "center",
            marginRight: 6,
        },
        statusItem: {
            flex: 1,
            justifyContent: "center",
            marginRight: 6,
            alignItems: "center",
        },
        buttonsRow: {
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            marginHorizontal: 15,
            marginVertical: 10,
        },
        leftButtons: {
            flexDirection: "row",
            alignItems: "center",
        },
        rightButtons: {
            flexDirection: "row",
            alignItems: "center",
        },
        loader: {
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "4%",
        },
        backButton: {
            alignSelf: "flex-end",
            margin: 0,
        },
        actionContainer: {
            width: 50,
            alignItems: "center",
            justifyContent: "center",
        },
        changedRow: {
            backgroundColor: "rgba(255, 193, 7, 0.12)",
        },
        deletedRow: {
            backgroundColor: "rgba(220, 53, 69, 0.12)",
        },
        previousRow: {
            backgroundColor: "rgba(108, 117, 125, 0.08)",
        },
        disabledRowContent: {
            opacity: 0.55,
        },
        savingOverlay: {
            ...StyleSheet.absoluteFillObject,
            backgroundColor: "rgba(255,255,255,0.35)",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 999,
        },
    });

export default HRAdminEmployeeRates;